import type { FC, ReactElement } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import type { AIAnswerQueryFunctionType } from '@atlassian/search-ai';

import { messages } from '../../../messages';
import { SearchDialogProduct } from '../../product-router/product/search-dialog-product';
import { useProductContext } from '../../product-router/product-router';
import { useDefaultSuppliers } from '../../result-supplier/default-suppliers';
import { type ExtensibleTabProps } from '../extensible-tab-types';
import {
	CONFLUENCE_PRODUCT_ID,
	ConfluenceFilters,
	type ConfluenceRecentsClientConfig,
	type ConfluenceSearchClientConfig,
	ConfluenceScope,
	NAVIGATION_V3_EXPERIENCE,
	PEOPLE_FILTER_LIMIT,
	SPACE_FILTER_LIMIT,
	type SupportedConfScopeResponses,
 type RendererOverrideTypes } from './types';
import { useConfluenceAdvancedSearchUrlFactory } from './utils';
import { useRecentsClient } from './recents-client';
import { type FilterLoader, type GenericFilterOption, type SpaceFilterOption } from '../../filters/filter-types';
import { useFilterStore } from '../../filters/filter-store';
import { useGenericFilters, useSiteFilterDefinition } from '../../filters/filter-context/filter-context';
import { FilterOptionSource } from '../../../common/filters/types';
import { useAnalytics } from '../../../common/analytics/use-analytics';
import {
	onSuggestedPromptClick,
	onPrequeryBannerDismiss,
	onPrequeryBannerShown,
} from '../../../common/analytics/events';
import { ProductStates } from '../../product-state-machine/product-state-machine-types';
import { useSearchConfigContext } from '../../../common/search-config-provider/search-config-provider';
import { defaultPermissionsSupplier } from './permission-supplier';

import { FilterOptionItem } from '../../product-router/product/result-provider/result-renderer/filter-section/filter-option-item';
import { useConfluenceSections } from './sections';
import { useConfluenceSuppliers } from './confluence-item-suppliers';
import { useConfluenceDateFilter } from './filter-by-date-experiment/use-confluence-date-filter';
import { useDialogExpansionContext } from '../../dialog-expansion-context/dialog-expansion-context';
import { useQuery } from '../../query-context/query-context';
import { useSessionUserInput } from '../../user-input-provider/user-input-context';
import { useQueryParams } from '../../products/confluence/confluence-analytics-query-params';
import { type AdvancedSearchUrlGenerator } from '../../product-router/product-router-state';
import { ExtensibleConfluenceTabFooter } from '../../advanced-search-footer/extensible-confluence-tab-footer';
import { getAtlassianHomeSearchUrl } from '../../advanced-search-footer/atlassian-home-search-link';
import { useUserContext } from '../../../common/user-context/user-context';
import { useTypedAggregatorClient } from '../../aggregator-client-context/aggregator-client-context';
import { useShowAtlassianHomeSearchLink } from '../../../common/show-atlassian-home-search-link';

export interface ExtensibleConfluenceTabProps extends ExtensibleTabProps {
	recentClientConfig: ConfluenceRecentsClientConfig;
	searchClientConfig?: ConfluenceSearchClientConfig;
	aiSearchConfig?: {
		aiSearchQueryFunction: AIAnswerQueryFunctionType;
		prequeryBannerEnabled?: boolean;
		dismissPrequeryBanner?: () => void;
	};
	formatDate?: (lastModified: string) => ReactElement;
	autocorrectEnabled?: boolean;
	additionalContentTypesEnabled?: boolean;
	rendererOverride?: RendererOverrideTypes;
	aiAdminNotificationHeader?: () => ReactElement;
}

const InteractiveSearchAIOnboardingBanner = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/search-ai/banner" */ '@atlassian/search-ai/banner'
	).then(({ InteractiveSearchAIOnboardingBanner }) => ({
		default: InteractiveSearchAIOnboardingBanner,
	})),
);

export const ExtensibleConfluenceTab: FC<ExtensibleConfluenceTabProps> = ({
	recentClientConfig,
	searchClientConfig,
	aiSearchConfig,
	formatDate,
	autocorrectEnabled,
	additionalContentTypesEnabled = false,
	rendererOverride,
	aiAdminNotificationHeader,
	...rest
}) => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { setQuery } = useQuery();
	const { toggleShowSearchAIDialog } = useSessionUserInput();
	const { confluenceSites } = useSearchConfigContext();
	const { defineFilters } = useGenericFilters();
	const { isUserAnonymous } = recentClientConfig;
	const { searchClient } = useRecentsClient(recentClientConfig);
	const { isExpanded } = useDialogExpansionContext();
	const appendAnalyticsQueryParams = useQueryParams();
	const { getPrimaryProduct } = useProductContext();

	const siteFilterDefinition = useSiteFilterDefinition(
		rest.workspaces || [],
		CONFLUENCE_PRODUCT_ID,
	);

	const { confluenceTabDateFilter, appliedDateFilter } = useConfluenceDateFilter();

	const spaceFilterStore = useFilterStore<SpaceFilterOption>(
		[],
		CONFLUENCE_PRODUCT_ID,
		ConfluenceFilters.Space,
	);
	const peopleFilterStore = useFilterStore<GenericFilterOption>(
		[],
		CONFLUENCE_PRODUCT_ID,
		ConfluenceFilters.People,
	);

	const { preQueryItemSupplier, postQueryItemSupplier } = useConfluenceSuppliers({
		recentClientConfig,
		searchClientConfig,
		autocorrectEnabled,
		spaceFilterStore,
		peopleFilterStore,
		isExpanded,
		isUserAnonymous,
		formatDate,
		rendererOverride,
		additionalContentTypesEnabled,
	});

	const sections = useConfluenceSections({
		formatDate,
		autocorrectEnabled,
		additionalContentTypesEnabled,
		rendererOverride,
	});

	const itemSuppliers = useDefaultSuppliers<SupportedConfScopeResponses, ConfluenceScope | string>(
		CONFLUENCE_PRODUCT_ID,
		sections,
		{
			preQueryItemSupplier: rest.preQueryItemSupplier || preQueryItemSupplier,
			postQueryItemSupplier: rest.postQueryItemSupplier || postQueryItemSupplier,
		},
	);

	const confluenceAdvancedSearchUrlFactory = useConfluenceAdvancedSearchUrlFactory(
		recentClientConfig.url,
		{
			spaceFilters: spaceFilterStore.availableFilters
				.filter((f) => f.isChecked)
				.map((f) => f.spaceKey || ''),
			peopleFilters: peopleFilterStore.availableFilters.filter((f) => f.isChecked).map((f) => f.id),
			dateFilter: !!appliedDateFilter ? [appliedDateFilter] : [],
		},
	);

	const { orgId } = useUserContext();
	const primaryProduct = getPrimaryProduct();
	const aggregatorClient = useTypedAggregatorClient();
	const cloudId = aggregatorClient?.getAggregatorConfig().cloudId;
	const showAtlassianHomeSearchLink = !!(
		useShowAtlassianHomeSearchLink(primaryProduct?.id) &&
		orgId &&
		cloudId
	);

	const generateAdvancedSearchUrl: AdvancedSearchUrlGenerator = useCallback(
		(query, baseUrl, usage) => {
			if (usage === 'onInputEnterModified' && showAtlassianHomeSearchLink) {
				return getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct?.id);
			}
			return confluenceAdvancedSearchUrlFactory(query, baseUrl);
		},
		[
			confluenceAdvancedSearchUrlFactory,
			orgId,
			cloudId,
			showAtlassianHomeSearchLink,
			primaryProduct?.id,
		],
	);

	const recentSectionTitleGenerator = useCallback((section: { title: string }) => {
		return section.title;
	}, []);

	const loadSpaceFilters: FilterLoader = useCallback(
		(query: string) => {
			return isUserAnonymous || !searchClient
				? Promise.resolve([])
				: searchClient
						.search({
							query,
							context: {
								sessionId: '',
								referrerId: null,
							},
							scopes: [ConfluenceScope.Space],
							modelParams: [],
							resultLimit: SPACE_FILTER_LIMIT,
							experience: NAVIGATION_V3_EXPERIENCE,
							sites: [],
						})
						.then(({ response }) => {
							const results = response.retrieveScope(ConfluenceScope.Space)?.results;
							return (
								results?.map((result) => ({
									value: {
										id: result.id,
										spaceKey: result.space?.key || '',
										avatarUrl: `${result.baseUrl}${result.space!.icon.path}`,
										label: result.title,
										filterSource: FilterOptionSource.SEARCH,
										isChecked: false,
										isVisible: true,
									},
									label: (
										<FilterOptionItem
											label={result.title}
											iconProps={{
												src: `${result.baseUrl}${result.space!.icon.path}`,
												appearance: 'square',
											}}
										/>
									),
								})) || []
							);
						});
		},
		[searchClient, isUserAnonymous],
	);

	const loadPeopleFilters: FilterLoader = useCallback(
		(query: string) => {
			return isUserAnonymous || !searchClient
				? Promise.resolve([])
				: searchClient
						.search({
							query,
							context: {
								sessionId: '',
								referrerId: null,
							},
							scopes: [ConfluenceScope.People],
							modelParams: [],
							resultLimit: PEOPLE_FILTER_LIMIT,
							experience: NAVIGATION_V3_EXPERIENCE,
							sites: [],
						})
						.then(({ response }) => {
							const results = response.retrieveScope(ConfluenceScope.People)?.results;
							return (
								results?.map((result) => ({
									value: {
										id: result.id,
										avatarUrl: result.avatarUrl,
										label: result.name,
										filterSource: FilterOptionSource.SEARCH,
										isChecked: false,
										isVisible: true,
									},
									label: (
										<FilterOptionItem label={result.name} iconProps={{ src: result.avatarUrl }} />
									),
								})) || []
							);
						});
		},
		[searchClient, isUserAnonymous],
	);

	const confluenceTabGenericFilters = useMemo(() => {
		return [
			{
				['@type']: ConfluenceFilters.Space,
				fieldName: 'spaceKeys',
				store: spaceFilterStore,
				loadFilterOptions: loadSpaceFilters,
				sectionLabel: formatMessage(messages.space_filters_title),
				findMoreLabel: formatMessage(messages.space_filters_find_more),
			},
			{
				['@type']: ConfluenceFilters.People,
				fieldName: 'accountIds',
				store: peopleFilterStore,
				loadFilterOptions: loadPeopleFilters,
				sectionLabel: formatMessage(messages.contributor_filters_title),
				findMoreLabel: formatMessage(messages.contributor_filters_find_more),
				filterConfig: {
					filterOptionIconShape: 'circle' as const,
				},
			},
		];
	}, [formatMessage, loadPeopleFilters, loadSpaceFilters, peopleFilterStore, spaceFilterStore]);

	const filtersToDefine = useMemo(() => {
		return [...confluenceTabDateFilter, ...siteFilterDefinition, ...confluenceTabGenericFilters];
	}, [confluenceTabGenericFilters, confluenceTabDateFilter, siteFilterDefinition]);

	const onPrequeryBannerRender = useCallback(() => {
		fireAnalyticsEvent(onPrequeryBannerShown());
	}, [fireAnalyticsEvent]);

	const onPromptSuggestionClick = useCallback(
		(prompt: string, promptId: string) => {
			fireAnalyticsEvent(onSuggestedPromptClick(promptId));
			setQuery(prompt);
			toggleShowSearchAIDialog(true);
		},
		[fireAnalyticsEvent, setQuery, toggleShowSearchAIDialog],
	);

	const dismissOnboardingBanner = useCallback(() => {
		if (aiSearchConfig?.dismissPrequeryBanner) {
			fireAnalyticsEvent(onPrequeryBannerDismiss());
			aiSearchConfig.dismissPrequeryBanner();
		}
	}, [aiSearchConfig, fireAnalyticsEvent]);

	const initializeFilters = useCallback(() => {
		defineFilters(CONFLUENCE_PRODUCT_ID, filtersToDefine);
	}, [defineFilters, filtersToDefine]);

	initializeFilters();

	return (
		<SearchDialogProduct
			id={CONFLUENCE_PRODUCT_ID}
			expandedStateInputPlaceholder={
				!!aiSearchConfig
					? formatMessage(messages.confluence_search_input_expanded_placeholder_ai)
					: formatMessage(messages.confluence_search_input_expanded_placeholder)
			}
			title={formatMessage(messages.confluence_tab_label)}
			autocorrectEnabled={autocorrectEnabled}
			sections={sections}
			generateAdvancedSearchUrl={generateAdvancedSearchUrl}
			preQuerySectionTitleGenerator={recentSectionTitleGenerator}
			showViewAllFiltersLink={true}
			{...rest}
			{...itemSuppliers}
			aiSearchConfig={aiSearchConfig}
			permissionSupplier={
				confluenceSites.length > 0 ? defaultPermissionsSupplier : rest.permissionSupplier
			}
			appendQueryParams={appendAnalyticsQueryParams}
		>
			{({ productState, ...props }) => {
				const Footer = showAtlassianHomeSearchLink && (
					<ExtensibleConfluenceTabFooter orgId={orgId} cloudId={cloudId} {...props} />
				);
				if (productState === ProductStates.PreQuerySuccess) {
					if (!!aiAdminNotificationHeader) {
						return {
							Header: aiAdminNotificationHeader,
							Footer,
						};
					}
					if (!!aiSearchConfig && aiSearchConfig.prequeryBannerEnabled) {
						return {
							Header: () => (
								<InteractiveSearchAIOnboardingBanner
									onPromptSuggestionClick={onPromptSuggestionClick}
									onXButtonClick={dismissOnboardingBanner}
									onBannerShown={onPrequeryBannerRender}
								/>
							),
							Footer,
						};
					}
				}

				return {
					Footer,
				};
			}}
		</SearchDialogProduct>
	);
};
